import { useState, useEffect, useCallback, useMemo, Suspense, useContext, lazy, useRef } from 'react';
import { FaCrown, FaMedal, FaTrophy } from '../utils/icons';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import toast from '../utils/toast';
import api from '../utils/api';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import ErrorBoundary from '../components/ErrorBoundary';
import { io } from 'socket.io-client';
import { config } from '../config/env';
import '../styles/leaderboard.css';

// Lazy load components
const LeaderboardHeader = lazy(() => import('../components/leaderboard/LeaderboardHeader'));
const StatsCards = lazy(() => import('../components/leaderboard/StatsCards'));
const UserRankCard = lazy(() => import('../components/leaderboard/UserRankCard'));
const LeaderboardTable = lazy(() => import('../components/leaderboard/LeaderboardTable'));
const LeftSidebar = lazy(() => import('../components/leaderboard/LeftSidebar'));

/**
* Renders the leaderboard interface with cached, filterable data, stats, and sidebar content while handling loading and user context.
* @example
* LeaderboardPage()
* <ErrorBoundary>...</ErrorBoundary>
* @param {{}} props - Component props (unused) for LeaderboardPage.
* @returns {{JSX.Element}} The rendered leaderboard dashboard layout.
**/
const Leaderboard = () => {
  const { currentUser } = useAuth();
  const { theme } = useContext(ThemeContext);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('global');
  const [timeFilter, setTimeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('points');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [userRank, setUserRank] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPoints: 0,
    averagePoints: 0,
    topPerformer: null
  });

  // Ref for intersection observer
  const sidebarRef = useRef(null);
  const socketRef = useRef(null);
  
  // Lazy load sidebar
  const showSidebar = useIntersectionObserver(sidebarRef);

  // Session cache helpers
  // NOTE: Disabled for real-time accuracy – we keep the helpers
  // as no-ops so future re‑enablement is easy, but the leaderboard
  // will always fetch fresh data (no stale cache).
  const CACHE_TTL_MS = 0;
  const getCache = (key) => {
    // Caching disabled – always return null
    return null;
  };
  const setCache = (key, data) => {
    // Caching disabled – no-op
    return;
  };

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);
      
      let data;

      // Weekly / Monthly use dedicated endpoints backed by Redis
      if (activeTab === 'weekly' || activeTab === 'monthly') {
        const endpoint = activeTab === 'weekly' ? '/api/leaderboard/weekly' : '/api/leaderboard/monthly';
        const params = new URLSearchParams({ page: 1, limit: 100 });
        const response = await api.get(`${endpoint}?${params.toString()}`);
        const apiData = response.data || {};

        // Normalise into the same shape as the main leaderboard API
        const leaderboard = (apiData.leaderboard || []).map((entry, index) => ({
          rank: entry.rank || index + 1,
          user: entry.user || {
            id: entry.userId,
            username: entry.username,
            avatar: entry.avatar
          },
          score: entry.score || entry.points || 0,
          points: entry.score || entry.points || 0,
          problemsSolved: entry.problemsSolved || 0,
          challengesWon: entry.challengesWon || 0,
          streak: entry.streak || 0,
          successRate: entry.successRate || 0,
          category: activeTab
        }));

        data = {
          leaderboard,
          userRank: null,
          stats: {
            totalUsers: apiData.totalUsers || 0,
            totalPoints: leaderboard.reduce((sum, u) => sum + (u.points || 0), 0),
            averagePoints:
              leaderboard.length > 0
                ? Math.round(
                    leaderboard.reduce((sum, u) => sum + (u.points || 0), 0) / leaderboard.length
                  )
                : 0,
            topPerformer: leaderboard[0]
              ? { username: leaderboard[0].user.username, points: leaderboard[0].points }
              : null
          }
        };
      } else {
        const params = new URLSearchParams({
          tab: activeTab === 'friends' ? 'global' : activeTab,
          timeFilter,
          sortBy,
          sortOrder,
          limit: 100,
          category:
            activeTab === 'problems'
              ? 'problems'
              : activeTab === 'challenges'
              ? 'challenges'
              : 'global'
        });

        const cacheKey = `leaderboard:${activeTab}:${timeFilter}:${sortBy}:${sortOrder}`;
        const cached = getCache(cacheKey);
        if (cached) {
          setLeaderboard(cached.leaderboard || []);
          setUserRank(cached.userRank || null);
          setStats(cached.stats || {});
          setLoading(false);
        }

        const response = await api.get(`/api/leaderboard?${params.toString()}`);
        data = response.data;

        // For Friends tab, filter client‑side to accepted friends only
        if (activeTab === 'friends' && currentUser?.friends?.length) {
          const friendIds = new Set(
            (currentUser.friends || [])
              .filter(f => f.status === 'accepted')
              .map(f => (f.user?._id || f.user || f._id)?.toString())
          );

          data.leaderboard =
            (data.leaderboard || []).filter(entry => {
              const uid = entry.user?.id || entry.user?._id;
              return uid && friendIds.has(uid.toString());
            }) || [];
        }

        setCache(cacheKey, data);
      }

      setLeaderboard(data.leaderboard || []);
      setUserRank(data.userRank || null);
      setStats(
        data.stats || {
          totalUsers: 0,
          totalPoints: 0,
          averagePoints: 0,
          topPerformer: null
        }
      );
    } catch (error) {
      
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, timeFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  // Real-time socket updates
  useEffect(() => {
    // Initialize socket connection
    socketRef.current = io(config.apiUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    // Join leaderboard room
    socketRef.current.emit('joinLeaderboard');

    // Helper to apply a single update payload to current leaderboard
    const applySingleUpdate = (update) => {
      setLeaderboard(prevLeaderboard => {
        if (!Array.isArray(prevLeaderboard) || prevLeaderboard.length === 0) {
          return prevLeaderboard;
        }

        const updatedLeaderboard = prevLeaderboard.map(entry => {
          const entryUserId = entry.user?._id || entry.user?.id;
          if (!entryUserId) return entry;

          if (entryUserId === update.userId) {
            return {
              ...entry,
              problemsSolved: update.problemsSolved,
              challengesWon: update.challengesSolved,
              successRate: update.successRate,
              streak: update.streak,
              user: {
                ...(entry.user || {}),
                username: update.username,
                avatar: update.avatar
              }
            };
          }
          return entry;
        });

        // Re-sort the leaderboard based on current sort settings
        return updatedLeaderboard.sort((a, b) => {
          const aValue = a[sortBy] || 0;
          const bValue = b[sortBy] || 0;
          return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
        });
      });
    };

    // Listen for single-user leaderboard updates
    socketRef.current.on('leaderboardUpdate', (data) => {
      if (data) {
        applySingleUpdate(data);
      }
    });

    // Listen for batched leaderboard updates
    socketRef.current.on('leaderboardBatchUpdate', (payload) => {
      if (!payload || !Array.isArray(payload.updates)) {
        return;
      }
      payload.updates.forEach(update => applySingleUpdate(update));
    });

    // Optional: handle full refresh payloads
    socketRef.current.on('leaderboardFullUpdate', (payload) => {
      if (!payload || !Array.isArray(payload.users)) return;

      const sorted = payload.users
        .map((user, index) => ({
          rank: index + 1,
          user: {
            id: user.userId,
            username: user.username,
            avatar: user.avatar
          },
          points: user.points,
          problemsSolved: user.problemsSolved,
          challengesWon: user.challengesSolved,
          successRate: user.successRate,
          streak: user.streak
        }))
        .sort((a, b) => {
          const aValue = a[sortBy] || 0;
          const bValue = b[sortBy] || 0;
          return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
        });

      setLeaderboard(sorted);
    });

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leaveLeaderboard');
        socketRef.current.disconnect();
      }
    };
  }, [sortBy, sortOrder]);

  // Memoized filtered leaderboard for performance
  const filteredLeaderboard = useMemo(() => {
    if (!searchTerm) return leaderboard;
    
    return leaderboard.filter(entry => {
      const username = entry.user?.username || entry.username || '';
      return username.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [leaderboard, searchTerm]);

  const getRankIcon = useCallback((rank) => {
    if (rank === 1) return <FaCrown className="text-yellow-500 text-2xl" />;
    if (rank === 2) return <FaMedal className="text-gray-400 text-2xl" />;
    if (rank === 3) return <FaMedal className="text-orange-500 text-2xl" />;
    if (rank <= 10) return <FaTrophy className="text-blue-500 text-xl" />;
    return <span className="text-gray-500 font-bold text-lg">#{rank}</span>;
  }, []);

  return (
    <ErrorBoundary>
      <div className="min-h-screen" style={{ backgroundColor: theme.background.primary, color: theme.text.primary }}>
        <div className="container mx-auto px-4 py-6">
          {/* Header */}
          <Suspense fallback={<div className="text-center mb-3"><h1 className="text-2xl font-bold mb-1">Leaderboard</h1></div>}>
            <LeaderboardHeader />
          </Suspense>

          {/* Stats Cards */}
          <Suspense fallback={<div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 h-16" />}>
            <StatsCards stats={stats} />
          </Suspense>

          {/* User Rank Card */}
          <Suspense fallback={userRank ? <div className="rounded-lg p-2 mb-3 h-14" /> : null}>
            <UserRankCard userRank={userRank} stats={stats} />
          </Suspense>

          {/* Main Content with Sidebar Layout */}
          <div className="flex gap-4">
            {/* Left Sidebar */}
            <div ref={sidebarRef} className="hidden lg:block">
              {showSidebar && (
                <Suspense fallback={<div className="w-72 h-96" />}>
                  <LeftSidebar leaderboard={filteredLeaderboard} />
                </Suspense>
              )}
            </div>

            {/* Main Leaderboard Table */}
            <div className="flex-1 min-w-0">
              <Suspense fallback={<div className="rounded-lg border overflow-hidden h-96" />}>
                <LeaderboardTable
                  leaderboard={filteredLeaderboard}
                  loading={loading}
                  currentUserId={currentUser?._id}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  timeFilter={timeFilter}
                  setTimeFilter={setTimeFilter}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  getRankIcon={getRankIcon}
                />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Leaderboard;

